import React, { useState } from 'react';
import { UserIcon, EnvelopeIcon, PhoneIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { createBooking, validateBookingData, formatDateForDisplay } from '../services/api';
import { toast } from 'react-toastify';
import LoadingSpinner from './LoadingSpinner';

const BookingForm = ({ settings, selectedDate, selectedTime, selectedGuests, onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: selectedGuests || 2
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Fehler für dieses Feld löschen wenn User tippt
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const bookingData = {
      ...formData,
      date: selectedDate,
      time: selectedTime
    };

    // Validierung
    const validation = validateBookingData(bookingData, settings);
    if (!validation.isValid) {
      setErrors(validation.errors);
      toast.error('Bitte korrigieren Sie die markierten Fehler');
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await createBooking(bookingData);
      
      // Toast entfernt - ConfirmationModal ist besser
      // toast.success(settings.confirmation_required ? 
      //   'Reservierungsanfrage gesendet! Sie erhalten eine E-Mail-Bestätigung.' :
      //   'Reservierung erfolgreich erstellt!'
      // );

      onSubmit({
        ...bookingData,
        id: result.id,
        status: 'pending'
      });

    } catch (error) {
      console.error('Booking creation failed:', error);
      toast.error(error.message || 'Fehler beim Erstellen der Reservierung');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 border-gray-300 p-6">
      <div className="flex items-center mb-8">
        <button
          onClick={onBack}
          className="mr-4 p-3 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
        </button>
        <h2 className="text-2xl font-bold text-gray-800">
          Kontaktdaten eingeben
        </h2>
      </div>

      {/* Buchungsübersicht */}
      <div className="bg-restaurant-background rounded-xl p-6 mb-8 border-2 border-restaurant-light">
        <h3 className="font-bold text-restaurant-text mb-4 text-xl">Ihre Auswahl:</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-base">
          <div>
            <span className="text-restaurant-muted font-semibold block mb-2 text-sm uppercase tracking-wide">Datum:</span>
            <p className="text-restaurant-text font-semibold text-lg">{formatDateForDisplay(selectedDate)}</p>
          </div>
          <div>
            <span className="text-restaurant-muted font-semibold block mb-2 text-sm uppercase tracking-wide">Uhrzeit:</span>
            <p className="text-restaurant-text font-semibold text-lg">{selectedTime} Uhr</p>
          </div>
          <div>
            <span className="text-restaurant-muted font-semibold block mb-2 text-sm uppercase tracking-wide">Personen:</span>
            <p className="text-restaurant-text font-semibold text-lg">{formData.guests} {formData.guests === 1 ? 'Person' : 'Personen'}</p>
          </div>
        </div>
      </div>

      {/* Formular */}
      <form onSubmit={handleSubmit} className="space-y-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-base font-semibold text-gray-700 mb-2">
              <UserIcon className="inline w-5 h-5 mr-2 text-gray-600" />
              Vollständiger Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-restaurant-primary focus:border-transparent text-base placeholder-gray-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ihr vollständiger Name"
              required
            />
            {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* E-Mail */}
          <div>
            <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-2">
              <EnvelopeIcon className="inline w-5 h-5 mr-2 text-gray-600" />
              E-Mail-Adresse *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-restaurant-primary focus:border-transparent text-base placeholder-gray-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="ihre.email@beispiel.de"
              required
            />
            {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
          </div>

          {/* Telefon */}
          <div>
            <label htmlFor="phone" className="block text-base font-semibold text-gray-700 mb-2">
              <PhoneIcon className="inline w-5 h-5 mr-2 text-gray-600" />
              Telefonnummer *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-restaurant-primary focus:border-transparent text-base placeholder-gray-500 ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ihre Telefonnummer"
              required
            />
            {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
          </div>

          {/* Personenanzahl */}
          <div>
            <label htmlFor="guests" className="block text-base font-semibold text-gray-700 mb-2">
              Anzahl Personen *
            </label>
            <select
              id="guests"
              name="guests"
              value={formData.guests}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-restaurant-primary focus:border-transparent text-base ${
                errors.guests ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            >
              {Array.from({length: settings.max_guests_per_booking}, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'Person' : 'Personen'}
                </option>
              ))}
            </select>
            {errors.guests && <p className="mt-2 text-sm text-red-600">{errors.guests}</p>}
          </div>
        </div>



        {/* Wichtige Hinweise */}
        <div className="bg-restaurant-background border border-restaurant-light rounded-xl p-6 mb-8">
          <h4 className="font-bold text-restaurant-text mb-4 text-lg">Wichtige Informationen zur Reservierung:</h4>
          <ul className="text-base text-restaurant-muted space-y-3">
            <li className="flex items-start">
              <span className="text-restaurant-primary mr-3 mt-1 flex-shrink-0">•</span>
              <span>
                <strong>Wichtig:</strong> Ihre Anfrage ist erst nach Bestätigung durch das Restaurant verbindlich. {settings.confirmation_required ? 
                  'Ihre Reservierung wird geprüft und per E-Mail bestätigt.' :
                  'Sie erhalten eine automatische Bestätigung per E-Mail.'
                }
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-restaurant-primary mr-3 mt-1 flex-shrink-0">•</span>
              <span>Bei Gruppen über {settings.max_guests_per_booking} Personen kontaktieren Sie uns bitte telefonisch unter der Telefonnummer 0521 173718</span>
            </li>
            <li className="flex items-start">
              <span className="text-restaurant-primary mr-3 mt-1 flex-shrink-0">•</span>
              <span>Stornierungen bitte mindestens 2 Stunden vor dem Termin</span>
            </li>
            <li className="flex items-start">
              <span className="text-restaurant-primary mr-3 mt-1 flex-shrink-0">•</span>
              <span>Bei Verspätung über 15 Minuten kann der Tisch nicht garantiert werden</span>
            </li>
          </ul>
        </div>

        {/* Datenschutz */}
        <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
          <p>
            Mit dem Absenden stimmen Sie zu, dass Ihre Daten zur Bearbeitung Ihrer Reservierung 
            verwendet werden. Weitere Informationen finden Sie in unserer <a href="https://www.kdw-bielefeld.de/datenschutz/" target="_blank" rel="noopener noreferrer" className="text-restaurant-primary">Datenschutzerklärung</a>.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 pt-6">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-base font-semibold"
          >
            Zurück
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-8 py-4 bg-restaurant-primary text-white rounded-lg hover:bg-restaurant-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base font-semibold"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Wird gesendet...
              </>
            ) : (
              'Unverbindlich anfragen'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm; 